import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Checkbox,
    Grid,
    Box,
    FormControlLabel,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { PlayingCard } from '../components/PlayingCard'; // ← adjust path if needed

const RARITIES = [
    'Common',
    'Uncommon',
    'Rare',
    'High Rare',
    'Super Rare',
    'Super Super Rare',
    'Legendary',
];

function LevelUpDialog({ open, onClose, card, userCards, onConfirm }) {
    const [selectedIds, setSelectedIds] = useState([]);
    const [rarityPick, setRarityPick] = useState('');

    // ---- Theme tokens for Beasts of Legend ----
    const GOLD = '#d4af37';
    const GOLD_SOFT = 'rgba(212,175,55,0.75)';
    const GOLD_DIM = 'rgba(212,175,55,0.6)';
    const BG_DARK = '#0a0a0a';
    const BG_PANEL = 'rgba(0,0,0,0.7)';
    const TEXT_WARM = '#f9f6e3';
    const CINZEL = `'Cinzel', serif`;

    const goldBtnSolid = {
        fontFamily: CINZEL,
        letterSpacing: 0.6,
        color: BG_DARK,
        background: `linear-gradient(180deg, ${GOLD_SOFT}, ${GOLD})`,
        border: `1px solid ${GOLD_DIM}`,
        boxShadow: `0 2px 12px rgba(212,175,55,0.25)`,
        '&:hover': {
            background: `linear-gradient(180deg, ${GOLD}, ${GOLD})`,
            boxShadow: `0 4px 20px rgba(212,175,55,0.35)`,
        },
    };

    const goldBtnOutline = {
        fontFamily: CINZEL,
        letterSpacing: 0.6,
        color: TEXT_WARM,
        border: `1px solid ${GOLD_DIM}`,
        background: 'transparent',
        boxShadow: `inset 0 0 0 1px rgba(212,175,55,0.15)`,
        '&:hover': {
            borderColor: GOLD,
            boxShadow: `0 2px 12px rgba(212,175,55,0.25), inset 0 0 0 1px ${GOLD}`,
            background: 'rgba(212,175,55,0.06)',
        },
    };

    const subtlePanel = {
        background: BG_PANEL,
        border: `1px solid ${GOLD_DIM}`,
        boxShadow: `0 0 24px rgba(212,175,55,0.15)`,
        borderRadius: 2,
    };

    const fieldChrome = {
        '& .MuiOutlinedInput-notchedOutline': { borderColor: GOLD_DIM },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
        '& .MuiSelect-icon': { color: GOLD_SOFT },
        color: TEXT_WARM,
    };

    const labelChrome = {
        color: GOLD_SOFT,
        '&.Mui-focused': { color: GOLD },
        fontFamily: CINZEL,
        letterSpacing: 0.5,
    };

    const classificationLabel = (cls) => {
        const c = String(cls || '').toLowerCase();
        if (c === 'man') return 'Man';
        if (c === 'beast') return 'Beast';
        return cls || '—';
    };
    const isMan = (c) => String(c?.classification || '').toLowerCase() === 'man';
    const isBeast = (c) => String(c?.classification || '').toLowerCase() === 'beast';

    const toggleSelection = (instanceId) => {
        setSelectedIds((prev) =>
            prev.includes(instanceId)
                ? prev.filter((id) => id !== instanceId)
                : [...prev, instanceId],
        );
    };

    const handleConfirm = () => {
        onConfirm(selectedIds);
        setSelectedIds([]);
        onClose();
    };

    const availableSacrifices = useMemo(
        () => userCards.filter((c) => !c.active && c.instanceId !== card.instanceId),
        [userCards, card?.instanceId],
    );

    // ---- selection helpers ----
    const idsByFilter = (fn) =>
        availableSacrifices.filter(fn).map((c) => c.instanceId);

    const selectOnly = (ids) => setSelectedIds(ids);
    const addAll = (ids) =>
        setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
    const clearAll = () => setSelectedIds([]);
    const selectAll = () => selectOnly(availableSacrifices.map((c) => c.instanceId));

    // rarity
    const onSelectByRarityAdd = () => {
        if (!rarityPick) return;
        addAll(idsByFilter((c) => c.rarity === rarityPick));
    };
    const onSelectByRarityOnly = () => {
        if (!rarityPick) return;
        selectOnly(idsByFilter((c) => c.rarity === rarityPick));
    };

    // level 1
    const onSelectLevel1Add = () => addAll(idsByFilter((c) => (c.level ?? 1) === 1));
    const onSelectLevel1Only = () => selectOnly(idsByFilter((c) => (c.level ?? 1) === 1));

    // race (classification)
    const countMan = availableSacrifices.filter(isMan).length;
    const countBeast = availableSacrifices.filter(isBeast).length;

    const onSelectManAdd = () => addAll(idsByFilter(isMan));
    const onSelectManOnly = () => selectOnly(idsByFilter(isMan));
    const onSelectBeastAdd = () => addAll(idsByFilter(isBeast));
    const onSelectBeastOnly = () => selectOnly(idsByFilter(isBeast));

    // counts
    const countForRarity = (r) =>
        availableSacrifices.filter((c) => c.rarity === r).length;
    const countLevel1 = availableSacrifices.filter((c) => (c.level ?? 1) === 1).length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: BG_DARK,
                    color: TEXT_WARM,
                    border: `1px solid ${GOLD_DIM}`,
                    boxShadow: `0 0 32px rgba(212,175,55,0.20)`,
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontFamily: CINZEL,
                    letterSpacing: 1,
                    fontWeight: 700,
                    color: GOLD,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.4), transparent)',
                    borderBottom: `1px solid ${GOLD_DIM}`,
                    pb: 2,
                }}
            >
                Sacrifice Cards to Level Up <strong style={{ color: TEXT_WARM }}>{card?.name}</strong>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5 }}>
                {/* Target card preview */}
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        ...subtlePanel,
                    }}
                >
                    <Box sx={{ width: 140, flexShrink: 0 }}>
                        <PlayingCard card={card} showLevel={true} showStats={true} />
                    </Box>
                    <Typography sx={{
                        color: GOLD_SOFT, fontFamily: CINZEL, letterSpacing: 0.5, flex: 1,
                        minWidth: 0,
                        ml: 10,
                        pr: 1,
                        overflow: 'hidden',
                        alignSelf: 'center',
                    }}>
                        Choose cards to offer. Gold glows mark your selections.
                    </Typography>
                </Box>

                {/* Selection toolbar */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{
                        mb: 2,
                        p: 1.5,
                        gap: 1.5,
                        ...subtlePanel,
                    }}
                >
                    {/* Rarity picker */}
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="rarity-select-label" sx={labelChrome}>
                            Rarity
                        </InputLabel>
                        <Select
                            labelId="rarity-select-label"
                            label="Rarity"
                            value={rarityPick}
                            onChange={(e) => setRarityPick(e.target.value)}
                            sx={fieldChrome}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        background: BG_DARK,
                                        color: TEXT_WARM,
                                        border: `1px solid ${GOLD_DIM}`,
                                    },
                                },
                            }}
                        >
                            <MenuItem value="">
                                <em style={{ color: GOLD_SOFT }}>None</em>
                            </MenuItem>
                            {RARITIES.map((r) => (
                                <MenuItem key={r} value={r}>
                                    <span style={{ color: TEXT_WARM }}>{r}</span>{' '}
                                    <span style={{ opacity: 0.8, marginLeft: 8 }}>({countForRarity(r)})</span>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onSelectByRarityAdd}
                            disabled={!rarityPick || countForRarity(rarityPick) === 0}
                            sx={goldBtnOutline}
                        >
                            Add all of rarity
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onSelectByRarityOnly}
                            disabled={!rarityPick || countForRarity(rarityPick) === 0}
                            sx={goldBtnSolid}
                        >
                            Only rarity
                        </Button>

                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ mx: 0.5, borderColor: GOLD_DIM, opacity: 0.6 }}
                        />

                        {/* Level 1 */}
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onSelectLevel1Add}
                            disabled={countLevel1 === 0}
                            sx={goldBtnOutline}
                        >
                            Add all Level 1 ({countLevel1})
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onSelectLevel1Only}
                            disabled={countLevel1 === 0}
                            sx={goldBtnSolid}
                        >
                            Only Level 1
                        </Button>

                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ mx: 0.5, borderColor: GOLD_DIM, opacity: 0.6 }}
                        />

                        {/* Race (classification) */}
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onSelectManAdd}
                            disabled={countMan === 0}
                            sx={goldBtnOutline}
                        >
                            Add all Man ({countMan})
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onSelectManOnly}
                            disabled={countMan === 0}
                            sx={goldBtnSolid}
                        >
                            Only Man
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onSelectBeastAdd}
                            disabled={countBeast === 0}
                            sx={goldBtnOutline}
                        >
                            Add all Beast ({countBeast})
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onSelectBeastOnly}
                            disabled={countBeast === 0}
                            sx={goldBtnSolid}
                        >
                            Only Beast
                        </Button>

                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ mx: 0.5, borderColor: GOLD_DIM, opacity: 0.6 }}
                        />

                        {/* All / Clear */}
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={selectAll}
                            disabled={availableSacrifices.length === 0}
                            sx={goldBtnOutline}
                        >
                            Select all
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            onClick={clearAll}
                            disabled={selectedIds.length === 0}
                            sx={{
                                fontFamily: CINZEL,
                                letterSpacing: 0.5,
                                color: GOLD_SOFT,
                                '&:hover': { color: GOLD },
                            }}
                        >
                            Clear
                        </Button>
                    </Stack>
                </Stack>

                {/* Sacrifice choices */}
                {availableSacrifices.length === 0 ? (
                    <Typography sx={{ color: GOLD_SOFT }}>No available cards to sacrifice.</Typography>
                ) : (
                    <Grid container spacing={2}>
                        {availableSacrifices.map((c) => {
                            const checked = selectedIds.includes(c.instanceId);
                            return (
                                <Grid item xs={12} sm={6} md={4} key={c.instanceId}>
                                    <Box
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => toggleSelection(c.instanceId)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleSelection(c.instanceId);
                                            }
                                        }}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: '2px solid',
                                            borderColor: checked ? GOLD : GOLD_DIM,
                                            cursor: 'pointer',
                                            height: '100%',
                                            background: 'rgba(0,0,0,0.6)',
                                            boxShadow: checked
                                                ? `0 0 18px rgba(212,175,55,0.35)`
                                                : `0 0 10px rgba(212,175,55,0.08)`,
                                            transition: 'box-shadow 120ms ease, border-color 120ms ease, transform 80ms ease',
                                            '&:hover': {
                                                borderColor: GOLD,
                                                boxShadow: `0 0 18px rgba(212,175,55,0.25)`,
                                                transform: 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                        <Box sx={{ width: '100%', mb: 1 }}>
                                            <PlayingCard card={c} />
                                        </Box>

                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 700,
                                                color: TEXT_WARM,
                                                fontFamily: CINZEL,
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {c.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: GOLD_SOFT }}>
                                            {classificationLabel(c.classification)} • {c.rarity}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, color: TEXT_WARM }}>
                                            Level: <strong>{c.level ?? 1}</strong>
                                        </Typography>

                                        <FormControlLabel
                                            sx={{ mt: 1, color: TEXT_WARM }}
                                            onClick={(e) => e.stopPropagation()}
                                            control={
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => toggleSelection(c.instanceId)}
                                                    sx={{
                                                        color: GOLD_DIM,
                                                        '&.Mui-checked': { color: GOLD },
                                                    }}
                                                />
                                            }
                                            label="Select"
                                        />
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: `1px solid ${GOLD_DIM}`,
                    background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))',
                    py: 1.5,
                }}
            >
                <Typography
                    sx={{ mr: 'auto', pl: 2, color: GOLD_SOFT, fontFamily: CINZEL, letterSpacing: 0.5 }}
                    variant="body2"
                >
                    Selected: <strong style={{ color: TEXT_WARM }}>{selectedIds.length}</strong>
                </Typography>
                <Button
                    onClick={onClose}
                    sx={{
                        ...goldBtnOutline,
                        mr: 1,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    color="primary"
                    variant="contained"
                    disabled={selectedIds.length === 0}
                    sx={goldBtnSolid}
                >
                    Confirm Sacrifice ({selectedIds.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default LevelUpDialog;
